// Секцията „Депозити" на хартията на личния отчет и на отчета на заведението.
//
// Без взети депозити НЕ рендира нищо — хартията остава досегашната. Редовете
// са по метод („Депозит — В брой", „Депозит — С карта"…) и „Общо депозити";
// под тях — колко е платено С депозит (редът „Плащане с депозит" в таблицата
// по видове плащания) и че взетите в брой са в „Сума за отчитане".
//
// Числата са ТОЧНО полетата от BE (`lib/reportDeposits.ts`) — нищо не се
// смята. Хартията е светла котва (`.report-doc-paper`) — същите `gray-*`
// класове като съседните таблици (`ReportTipsSection`).

import { useTranslation } from "react-i18next";

import type { PaymentBreakdownItem, ReportDepositFields } from "../../../../../api/reports";
import {
  depositPaymentAmountOf,
  hasReportDeposits,
  reportDepositsOf,
} from "../../../../../lib/reportDeposits";
import type { ReportWithTips } from "../../../../../lib/reportTips";
import { ReportAmountToReport } from "./ReportAmountToReport";

type Props = {
  report: ReportDepositFields &
    Pick<ReportWithTips, "amount_to_report" | "cash_amount"> & {
      payment_breakdown?: PaymentBreakdownItem[] | null;
    };
  /** Отчетът на заведението без бакшиш: „Сума за отчитане:" е тук (при
   *  бакшиш я показва секцията „Бакшиш"). */
  withAmountToReport?: boolean;
};

const formatAmount = (amount: number) => amount.toFixed(2);

export const ReportDepositsSection = ({ report, withAmountToReport = false }: Props) => {
  const { t, i18n } = useTranslation();
  if (!hasReportDeposits(report)) return null;

  const isEnglish = (i18n.language ?? "bg").toLowerCase().startsWith("en");
  const deposits = reportDepositsOf(report);
  const paidWithDeposit = depositPaymentAmountOf(report.payment_breakdown);

  return (
    <div className="mb-6" data-testid="report-deposits-section">
      <h3 className="text-base font-bold text-gray-800 bg-gray-200 px-2 py-1 mb-2">
        {t("reportDeposits.section")}
      </h3>
      <table className="w-full border-collapse border border-gray-400 text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-400 px-2 py-1 text-left">
              {t("reportDeposits.colMethod")}
            </th>
            <th className="border border-gray-400 px-2 py-1 text-center">
              {t("reportDeposits.colCount")}
            </th>
            <th className="border border-gray-400 px-2 py-1 text-right">
              {t("reportDeposits.colAmount")}
            </th>
          </tr>
        </thead>
        <tbody>
          {deposits.rows.map((row) => (
            <tr key={row.payment_method_id}>
              <td className="border border-gray-400 px-2 py-1">
                {t("reportDeposits.taken", {
                  method: isEnglish && row.name_en ? row.name_en : row.name_bg,
                })}
              </td>
              <td className="border border-gray-400 px-2 py-1 text-center">{row.count}</td>
              <td className="border border-gray-400 px-2 py-1 text-right">
                {formatAmount(Number(row.amount))}
              </td>
            </tr>
          ))}
          <tr className="bg-gray-100 font-bold">
            <td className="border border-gray-400 px-2 py-1" colSpan={2}>
              {t("reportDeposits.total")}
            </td>
            <td className="border border-gray-400 px-2 py-1 text-right">
              {formatAmount(deposits.total)}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="mt-2 flex flex-col gap-1 text-sm">
        {deposits.cash !== 0 && (
          <div className="flex justify-between gap-3">
            <span className="text-gray-600">{t("reportDeposits.cashInAmountToReport")}</span>
            <span className="font-semibold">{formatAmount(deposits.cash)}</span>
          </div>
        )}
        {paidWithDeposit !== 0 && (
          <div className="flex justify-between gap-3">
            <span className="text-gray-600">{t("reportDeposits.paidWithDeposit")}</span>
            <span className="font-semibold">{formatAmount(paidWithDeposit)}</span>
          </div>
        )}
      </div>

      {withAmountToReport ? (
        <div className="mt-4 border-t-2 border-gray-800 pt-2 text-sm">
          <ReportAmountToReport report={report} />
        </div>
      ) : null}
    </div>
  );
};
